import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {FormControl} from '@material-ui/core';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import cn from 'classnames';
import axios from 'axios';
import DashboardContent from 'components/DashboardContent/styles';
import DashboardHeader from 'components/DashboardHeader/styles';
import Headline from 'components/Headline/styles';
import ByocProgressBar from 'components/ByocProgressBar';
import {
  ByocOnboardingContainer,
  ByocOnboardingTitle,
  ByocOnboardingText,
  ByocOnboardingLoadingOverlay
} from 'routes/App/byocOnboarding/styles';
import {
  OrderSourceContainer,
  OrderSourceField,
  ConnectButton,
  PlatformsSection,
  PlatformCard,
  PlatformLogo,
  PlatformButton,
  CommercialAgreementsSection,
  LabelWithInfo
} from 'routes/App/byocOnboarding/OrderSourceIntegration/styles';
import {ButtonOutline, ButtonPrimary} from 'components/buttons';
import ButtonInfo from 'components/buttons/ButtonInfo/styles';
import DialogAction from 'components/DialogAction/styles';
import DialogActions from 'components/DialogActions/styles';
import FieldLabel from 'components/FieldLabel/styles';
import {PROGRESS_BAR_STEPS} from 'helpers/constants/byocOnboarding';
import SelectWrapper from 'components/select/SelectComponentStyles';
import {ErrorSign, ErrorSignWrapper} from 'components/errorSign/errorSign';
import IconUEProvider from 'assets/selfDelivery/icon_ue_provider.svg';
import IconGHProvider from 'assets/selfDelivery/icon_gh_provider.svg';
import IconDDProvider from 'assets/selfDelivery/icon_dd_provider.svg';
import UberEatsCommission from 'assets/uber-eats.svg';
import DoordashCommission from 'assets/icon-doordash.svg';
import GrubhubCommission from 'assets/icon-grubhub.svg';
import {getStorageToken} from 'helpers/localStorage/authStorage';
import FieldInput from 'components/FieldInput';
import {DashboardField} from 'routes/App/dashboard/styles';
import {
  SERVER_URL,
  STREAM_ADAPTER_SERVICE_URL
} from 'helpers/services/constants/API_CONSTANTS';
import {
  UPDATE_BYOC_ONBOARDING_STEP,
  UPDATE_BYOC_IS_CONNECTED_TO_POS,
  GET_BYOC_AGREEMENT
} from 'store/byocOnboarding/actions';
import {updateByocPlatformsApi} from 'helpers/services/api/byocOnboarding';
import Loading from 'components/shared/Utils/Loading';
import {
  getMethodByCode,
  getOptions,
  newLogger
} from 'services/logger/new-logger';
import {scrollToFirstError} from 'helpers/functionUtils/scrollToFirstError';
import store from '../../../../store';
import {twoDecimalsFormatter} from '../../../../helpers/functionUtils/helpFunctions';
import {EmailMarketingSpinner} from '../../emailMarketing/components/styles';

// Format phone number to +1XXXXXXXXXX format
// Removes all non-digit characters and ensures +1 prefix
const formatPhoneToPlusOne = (phone) => {
  if (!phone) return '+1';

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  if (!digits) return '+1';

  // If starts with 1 and has 11 digits (country code already included), remove leading 1
  if (digits.startsWith('1') && digits.length === 11) {
    return `+1${digits.slice(1)}`;
  }

  // If starts with 1 but has more than 11 digits, keep only last 10 digits
  if (digits.startsWith('1') && digits.length > 11) {
    return `+1${digits.slice(-10)}`;
  }

  // For 10-digit numbers or numbers not starting with 1, add +1 prefix
  // If number is longer than 10, take last 10 digits
  const phoneDigits = digits.length > 10 ? digits.slice(-10) : digits;
  return `+1${phoneDigits}`;
};

const getDataForLogs = () => {
  const {user} = store.getState();
  return {
    locationId: user?.locationId,
    locationName: user?.locationName,
    userData: user?.userData
  };
};

// Map platform IDs to backend format for ConnectedPlatforms
const mapPlatformsToBackendFormat = (platforms) => {
  const platformMap = {
    uber: 'UberEats',
    grubhub: 'Grubhub',
    doordash: 'DoorDash'
  };

  return platforms.map((platform) => platformMap[platform] || platform);
};

const PLATFORMS = [
  {
    id: 'uber',
    name: 'Uber Eats',
    logo: IconUEProvider,
    commissionLogo: UberEatsCommission
  },
  {
    id: 'grubhub',
    name: 'GrubHub',
    logo: IconGHProvider,
    commissionLogo: GrubhubCommission
  },
  {
    id: 'doordash',
    name: 'DoorDash',
    logo: IconDDProvider,
    commissionLogo: DoordashCommission
  }
];

const OrderSourceIntegration = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const {data, loading, globalLoading} = useSelector(
    (state) => state.byocOnboardingReducer || {}
  );
  const orderSourceData =
    data?.orderSourceIntegration || data?.OrderSourceIntegration || {};

  const [receiveOrdersVia, setReceiveOrdersVia] = useState(
    orderSourceData?.channel || orderSourceData?.Channel || 'Sauce orders'
  );
  const [selectedPlatforms, setSelectedPlatforms] = useState(
    (
      orderSourceData?.selectedPlatforms ||
      orderSourceData?.SelectedPlatforms ||
      []
    ).filter((p) => p != null)
  );
  const [connectedPlatforms, setConnectedPlatforms] = useState(
    (
      orderSourceData?.connectedPlatforms ||
      orderSourceData?.ConnectedPlatforms ||
      []
    ).filter((p) => p != null)
  );
  const [connectingPlatforms, setConnectingPlatforms] = useState({});
  const [platformError, setPlatformError] = useState('');
  const [isPOSConnected, setIsPOSConnected] = useState(
    orderSourceData?.isConnectedtoPos ||
      orderSourceData?.IsConnectedtoPos ||
      false
  );
  const [posError, setPosError] = useState('');
  const [isProviderConnected, setIsProviderConnected] = useState(
    (orderSourceData?.connectedPlatforms?.length ||
      orderSourceData?.ConnectedPlatforms?.length ||
      0) > 0
  );
  const [providerError, setProviderError] = useState('');
  const [isGetAgreementClicked, setIsGetAgreementClicked] = useState(
    orderSourceData?.agreementInfo?.isAgreementPageGenerated ||
      orderSourceData?.AgreementInfo?.IsAgreementPageGenerated ||
      orderSourceData?.agreementInfo?.isGetButtonClicked ||
      orderSourceData?.AgreementInfo?.IsGetButtonClicked ||
      false
  );
  // Convert platformCommissions array to object for easier UI handling
  const getCommissionsFromData = (d) => {
    const commissionsObj = {
      uber: '',
      grubhub: '',
      doordash: ''
    };
    if (d?.platformCommissions && Array.isArray(d.platformCommissions)) {
      d.platformCommissions.forEach((item) => {
        if (item.platform && item.commissionValue !== undefined) {
          commissionsObj[item.platform] = item.commissionValue;
        }
      });
    }
    return commissionsObj;
  };

  const [commissions, setCommissions] = useState(() =>
    getCommissionsFromData(orderSourceData)
  );

  // Update state when data changes
  useEffect(() => {
    const channel = orderSourceData?.channel || orderSourceData?.Channel;
    if (channel) {
      setReceiveOrdersVia(channel);
    }
    const selected =
      orderSourceData?.selectedPlatforms || orderSourceData?.SelectedPlatforms;
    if (selected) {
      setSelectedPlatforms(selected.filter((p) => p != null));
    }
    const connected =
      orderSourceData?.connectedPlatforms ||
      orderSourceData?.ConnectedPlatforms;
    if (connected) {
      const filteredConnected = connected.filter((p) => p != null);
      setConnectedPlatforms(filteredConnected);
      setIsProviderConnected(filteredConnected.length > 0);
    }
    const isConnected =
      orderSourceData?.isConnectedtoPos !== undefined
        ? orderSourceData.isConnectedtoPos
        : orderSourceData?.IsConnectedtoPos;
    if (isConnected !== undefined) {
      setIsPOSConnected(isConnected);
    }
    const agreementInfo =
      orderSourceData?.agreementInfo || orderSourceData?.AgreementInfo;
    if (agreementInfo?.isAgreementPageGenerated !== undefined) {
      setIsGetAgreementClicked(agreementInfo.isAgreementPageGenerated);
    } else if (agreementInfo?.IsAgreementPageGenerated !== undefined) {
      setIsGetAgreementClicked(agreementInfo.IsAgreementPageGenerated);
    } else if (agreementInfo?.isGetButtonClicked !== undefined) {
      setIsGetAgreementClicked(agreementInfo.isGetButtonClicked);
    } else if (agreementInfo?.IsGetButtonClicked !== undefined) {
      setIsGetAgreementClicked(agreementInfo.IsGetButtonClicked);
    }
    // Update commissions when data changes
    const updatedCommissions = getCommissionsFromData(orderSourceData);
    setCommissions(updatedCommissions);
  }, [orderSourceData]);

  const receiveOrdersOptions = [
    {_id: 'Sauce orders', FullName: 'Sauce orders'}
  ];

  const handlePlatformToggle = (platformId) => {
    setPlatformError('');

    let newSelectedPlatforms;
    if (selectedPlatforms.includes(platformId)) {
      newSelectedPlatforms = selectedPlatforms
        .filter((id) => id !== platformId)
        .filter((id) => id != null);
      // If platform is removed from selected, also remove from connected
      if (connectedPlatforms.includes(platformId)) {
        const newConnectedPlatforms = connectedPlatforms
          .filter((id) => id !== platformId)
          .filter((id) => id != null);
        setConnectedPlatforms(newConnectedPlatforms);
      }
      // Clear commission when platform is deselected
      setCommissions((prev) => ({
        ...prev,
        [platformId]: ''
      }));
    } else {
      newSelectedPlatforms = [...selectedPlatforms, platformId];
    }

    setSelectedPlatforms(newSelectedPlatforms);
  };

  const handleCommissionChange = (platformId, value) => {
    setCommissions((prev) => ({
      ...prev,
      [platformId]: value
    }));
  };

  // Convert commissions object to platformCommissions array format for backend
  const convertCommissionsToBackendFormat = (commissionsObj) => {
    return Object.entries(commissionsObj)
      .filter(([_, value]) => value && value !== '')
      .map(([platform, value]) => ({
        platform,
        commissionValue: parseFloat(value) || 0
      }));
  };

  const handleConnectPlatform = async (platformId, isConnectPos) => {
    setProviderError('');
    setConnectingPlatforms((prev) => ({...prev, [platformId]: true}));

    try {
      const response = await axios.post(
        `${STREAM_ADAPTER_SERVICE_URL}/api/stream-adapter/sso/v1/perform-sso-connection`,
        {
          email: user?.userData?.email,
          fullName: `${user?.userData?.first_name || 'New'} ${
            user?.userData?.last_name || 'User'
          }`,
          phoneNumber: formatPhoneToPlusOne(user?.activeLocation?.Phone),
          providerOrgId: user?.activeLocation?._id,
          providerOrgName:
            user?.activeLocation?.BusinessName ||
            user?.activeLocation?.FullName,
          providerOrgRole: 'owner',
          merchantId: user?.userData?.merchant_id,
          userId: user?.userData?._id
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStorageToken()?.token || ''}`
          },
          responseType: 'text'
        }
      );

      newLogger(
        getOptions(response, false, getDataForLogs()),
        `Fetch finished ${
          response?.status
        } ${response?.config.method?.toUpperCase()} ${
          response?.config.url || 'unknown_url'
        }`,
        getMethodByCode(response?.status)
      );

      if (!response || response.status !== 200) {
        if (isConnectPos) {
          setPosError('Failed to connect to Self Delivery. Please try again.');
        } else {
          setProviderError(
            'Failed to connect to Self Delivery. Please try again.'
          );
        }
        return;
      }

      // Get the response as text (link)
      const link = response.data;

      if (link && link.trim()) {
        // Open the link in a new tab
        window.open(link.trim(), '_blank');

        if (isConnectPos) {
          setIsPOSConnected(true);
          setPosError('');
        } else {
          setPlatformError('');
        }

        // Add platform to connected platforms (only in state, not saved yet)
        if (platformId && !connectedPlatforms.includes(platformId)) {
          const newConnectedPlatforms = [
            ...connectedPlatforms.filter((p) => p != null),
            platformId
          ];
          setConnectedPlatforms(newConnectedPlatforms);
          setIsProviderConnected(newConnectedPlatforms.length > 0);
        }
      } else if (isConnectPos) {
        setPosError('Failed to get connection link. Please try again later');
      } else {
        setProviderError(
          'Failed to get connection link. Please try again later'
        );
      }
    } catch (error) {
      const {response} = error;

      newLogger(
        getOptions(response, true, getDataForLogs()),
        `Fetch finished ${
          response?.status
        } ${response?.config.method?.toUpperCase()} ${
          response?.config.url || 'unknown_url'
        }`,
        getMethodByCode(response?.status)
      );

      if (response?.data?.message) {
        if (isConnectPos) {
          setPosError(response.data.message);
        } else {
          setPlatformError(response.data.message);
        }
      } else if (isConnectPos) {
        setPosError('Failed to connect POS. Please try again later');
      } else {
        setPlatformError('Failed to connect platform. Please try again later');
      }
    } finally {
      setConnectingPlatforms((prev) => ({...prev, [platformId]: false}));
    }
  };

  // Scroll to first error when errors appear
  useEffect(() => {
    if (posError || platformError || providerError) {
      // Use longer delay to ensure React has rendered the error elements
      const timeoutId = setTimeout(() => {
        let elementToScroll = null;

        // Priority 1: Find posError by data attribute
        if (posError) {
          const posErrorElement = document.querySelector(
            '[data-error-type="pos-error"]'
          );
          if (posErrorElement && posErrorElement.offsetParent !== null) {
            const orderSourceField = posErrorElement.closest(
              '[class*="OrderSourceField"]'
            );
            elementToScroll = orderSourceField || posErrorElement;
          }
        }

        // Priority 2: Find platformError or providerError
        if (!elementToScroll && (platformError || providerError)) {
          const platformErrorElement = document.querySelector(
            '[data-error-type="platform-error"], [data-error-type="provider-error"]'
          );
          if (
            platformErrorElement &&
            platformErrorElement.offsetParent !== null
          ) {
            const orderSourceField = platformErrorElement.closest(
              '[class*="OrderSourceField"]'
            );
            elementToScroll = orderSourceField || platformErrorElement;
          }
        }

        // Fallback: find any visible error wrapper
        if (!elementToScroll) {
          const allErrorWrappers = Array.from(
            document.querySelectorAll('[class*="ErrorSignWrapper"]')
          );
          const firstVisible = allErrorWrappers.find((el) => {
            const isVisible = el.offsetParent !== null;
            const hasText = el.textContent?.trim() || el.innerText?.trim();
            return isVisible && hasText;
          });
          if (firstVisible) {
            const orderSourceField = firstVisible.closest(
              '[class*="OrderSourceField"]'
            );
            elementToScroll = orderSourceField || firstVisible;
          }
        }

        // Scroll to element - use scrollIntoView for reliable centering
        if (elementToScroll) {
          elementToScroll.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 600);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [posError, platformError, providerError]);

  const handleContinue = async () => {
    // Clear previous errors
    setPosError('');
    setPlatformError('');
    setProviderError('');

    if (!isPOSConnected) {
      setPosError('Please connect your POS system to continue');
      return;
    }

    if (connectedPlatforms.length === 0) {
      setPlatformError('Please connect at least one platform to continue');
      return;
    }

    const locationId = user?.locationId;

    if (!locationId) {
      setPlatformError('Location ID is required');
      return;
    }

    try {
      // Filter out null/undefined values
      const filteredSelected = selectedPlatforms.filter((p) => p != null);
      const filteredConnected = connectedPlatforms.filter((p) => p != null);

      const platformCommissions = convertCommissionsToBackendFormat(
        commissions
      );

      // Then save all data
      dispatch(
        UPDATE_BYOC_ONBOARDING_STEP({
          orderSourceIntegration: {
            channel: receiveOrdersVia,
            isConnectedtoPos: isPOSConnected,
            selectedPlatforms: filteredSelected,
            connectedPlatforms: filteredConnected,
            platformCommissions,
            isFinished: true
          },
          successCallback: () => {
            history.push('/byoc-onboarding/payment-details');
          }
        })
      );
    } catch (error) {
      console.log(error);
      setPlatformError('Failed to update platforms. Please try again.');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <DashboardContent className='DashboardContent'>
      <DashboardHeader>
        <Headline className='mTop5'>Self Delivery</Headline>
      </DashboardHeader>
      {globalLoading ? (
        <EmailMarketingSpinner>
          <Loading />
        </EmailMarketingSpinner>
      ) : (
        <>
          <ByocProgressBar currentStep={2} steps={PROGRESS_BAR_STEPS} />
          <OrderSourceContainer>
            <ByocOnboardingTitle>Order Source Integration</ByocOnboardingTitle>

            <OrderSourceField className='mTop20'>
              <LabelWithInfo>
                <FieldLabel className='FieldLabelRegular marginB0'>
                  <span>*</span> Receive orders via
                </FieldLabel>
                <ButtonInfo className='Outline ButtonInfoDrop marginRight4l'>
                  <div className='DropInfo DropInfoCenter pointer-events-none DropInfoLeftMob Small'>
                    Where would you like to receive and track your 3rd party
                    platform orders.
                  </div>
                </ButtonInfo>
              </LabelWithInfo>
              <SelectWrapper className='SelectMd w100'>
                <FormControl className='select-formcontrol'>
                  <Select
                    value={receiveOrdersVia}
                    name='receiveOrdersVia'
                    onChange={(e) => setReceiveOrdersVia(e.target.value)}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      },
                      getContentAnchorEl: null
                    }}
                  >
                    {receiveOrdersOptions.map((option) => (
                      <MenuItem key={option._id} value={option._id}>
                        {option.FullName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </SelectWrapper>
            </OrderSourceField>

            <OrderSourceField className='mTop35 positionRelative'>
              <FieldLabel className='FieldLabelRegular'>
                <span>*</span> Connect POS
              </FieldLabel>
              <ConnectButton>
                <ButtonPrimary
                  className='ButtonMedium'
                  onClick={() => {
                    handleConnectPlatform(null, true);
                  }}
                  type='button'
                >
                  <span>Connect POS</span>
                </ButtonPrimary>
              </ConnectButton>
              {posError && (
                <ErrorSignWrapper
                  className='mTop20 left0'
                  data-error-type='pos-error'
                >
                  <ErrorSign>{posError}</ErrorSign>
                </ErrorSignWrapper>
              )}
            </OrderSourceField>

            <hr style={{marginTop: '35px'}} />

            <OrderSourceField className='mTop35 positionRelative'>
              <FieldLabel className='FieldLabelRegular'>
                <span>*</span> Connected platforms
              </FieldLabel>
              <ByocOnboardingText className='mTop10'>
                Select platforms you are currently working with
              </ByocOnboardingText>
              <PlatformsSection>
                {PLATFORMS.map((platform) => {
                  const isSelected = selectedPlatforms.includes(platform.id);
                  const isConnected = connectedPlatforms.includes(platform.id);
                  const isConnecting =
                    connectingPlatforms[platform.id] || false;

                  return (
                    <PlatformCard
                      key={platform.id}
                      $isSelected={isSelected}
                      onClick={() => handlePlatformToggle(platform.id)}
                    >
                      <PlatformLogo
                        className={cn('', {bSize: platform.id === 'grubhub'})}
                      >
                        <img src={platform.logo} alt='logo' />
                      </PlatformLogo>
                      {isSelected && (
                        <PlatformButton className='flexCenter'>
                          <ButtonPrimary
                            className={cn('SDConnectProvider', {
                              disabled: !isPOSConnected || isConnecting
                            })}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isPOSConnected && !isConnecting) {
                                handleConnectPlatform(platform.id);
                              }
                            }}
                            type='button'
                            disabled={!isPOSConnected || isConnecting}
                          >
                            <span>
                              {isConnecting
                                ? 'Connecting...'
                                : 'Connect to Self Delivery'}
                            </span>
                          </ButtonPrimary>
                        </PlatformButton>
                      )}
                    </PlatformCard>
                  );
                })}
              </PlatformsSection>
              {platformError && (
                <ErrorSignWrapper
                  className='mTop15 left0'
                  data-error-type='platform-error'
                >
                  <ErrorSign>{platformError}</ErrorSign>
                </ErrorSignWrapper>
              )}
              {providerError && (
                <ErrorSignWrapper
                  className='mTop15 left0'
                  data-error-type='provider-error'
                >
                  <ErrorSign>{providerError}</ErrorSign>
                </ErrorSignWrapper>
              )}
            </OrderSourceField>

            {selectedPlatforms.length > 0 && (
              <OrderSourceField className='mTop35'>
                <FieldLabel className='FieldLabelRegular'>
                  Commission Paid
                </FieldLabel>
                <ByocOnboardingText className='mTop10'>
                  Specify how much commission you are paying each 3rd party on
                  your existing marketplace agreement.
                </ByocOnboardingText>
                <PlatformsSection style={{justifyContent: 'space-around'}}>
                  {PLATFORMS.map((platform) => {
                    const isSelected = selectedPlatforms.includes(platform.id);
                    if (!isSelected) {
                      // Return empty placeholder to maintain layout order
                      return (
                        <div
                          key={platform.id}
                          style={{
                            width: '150px',
                            visibility: 'hidden',
                            margin: '0 25px'
                          }}
                        />
                      );
                    }
                    return (
                      <PlatformCard
                        key={platform.id}
                        className='commission wAutoSpace paddingL0 paddingB0'
                        style={{maxWidth: '150px'}}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '8px',
                            marginRight: '15px'
                          }}
                        >
                          <img
                            src={platform.commissionLogo}
                            alt={platform.name}
                            style={{
                              objectFit: 'contain'
                            }}
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <FieldInput
                            type='text'
                            value={commissions[platform.id] || ''}
                            onChange={(e) => {
                              const result = twoDecimalsFormatter(
                                e.target.value,
                                commissions[platform.id],
                                100,
                                true
                              );
                              handleCommissionChange(platform.id, result);
                            }}
                            placeholder='0'
                            style={{flex: 1, width: '100px'}}
                          />
                          <span style={{fontSize: '14px', color: '#2f3d48'}}>
                            %
                          </span>
                        </div>
                      </PlatformCard>
                    );
                  })}
                </PlatformsSection>
              </OrderSourceField>
            )}

            <CommercialAgreementsSection className='mTop35'>
              <FieldLabel className='FieldLabelRegular'>
                Commercial Agreements
              </FieldLabel>
              <ByocOnboardingText className='mTop16'>
                Click if you don&apos;t have a Self Delivery agreement with at
                least one of the providers you want to connect with
              </ByocOnboardingText>
              <ConnectButton>
                <ButtonPrimary
                  className={cn('ButtonMedium', {
                    disabled:
                      !isPOSConnected ||
                      !selectedPlatforms?.length ||
                      !isProviderConnected
                  })}
                  onClick={async () => {
                    if (isPOSConnected && selectedPlatforms?.length) {
                      const locationId = user?.locationId;

                      if (!locationId) {
                        setPlatformError('Location ID is required');
                        return;
                      }

                      // Filter out null/undefined values
                      const filteredSelected = selectedPlatforms.filter(
                        (p) => p != null
                      );
                      const filteredConnected = connectedPlatforms.filter(
                        (p) => p != null
                      );

                      // Map platforms to backend format for ConnectedPlatforms

                      // Send three requests sequentially via saga
                      dispatch(
                        GET_BYOC_AGREEMENT({
                          ConnectedPlatforms: filteredConnected,
                          SelectedPlatforms: filteredSelected,
                          IsGetButtonClicked: true, // Keep for backward compatibility with old API
                          successCallback: () => {
                            setIsGetAgreementClicked(true);
                          }
                        })
                      );
                    }
                  }}
                  type='button'
                  disabled={!isPOSConnected || !selectedPlatforms?.length}
                >
                  <span>Get Agreement</span>
                </ButtonPrimary>
              </ConnectButton>
            </CommercialAgreementsSection>

            <DialogActions className='justifyEnd mTop32'>
              <DialogAction className='w30'>
                <ButtonOutline
                  onClick={() => history.push('/byoc-onboarding/menu-setup')}
                  type='button'
                >
                  <span>Back</span>
                </ButtonOutline>
              </DialogAction>
              <DialogAction className='w30'>
                <ButtonPrimary
                  onClick={handleContinue}
                  type='button'
                  disabled={loading}
                >
                  <span>Continue</span>
                </ButtonPrimary>
              </DialogAction>
            </DialogActions>
            {loading && (
              <ByocOnboardingLoadingOverlay>
                <Loading />
              </ByocOnboardingLoadingOverlay>
            )}
          </OrderSourceContainer>
        </>
      )}
    </DashboardContent>
  );
};

export default OrderSourceIntegration;
